import { Component, computed, inject } from '@angular/core';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { isAllowed, translate } from '../../../../../../shared/shared.global-service';
import { successDeleted } from '../../../../../content.error';
import { ConfirmBuilder } from '../../../../../../shared/service/confirm.builder';
import { TableComponent } from '../../../../../../shared/components/kit/table/table.component';
import { numberField, validFromColumn, validToColumn } from '../../../../../../shared/components/kit/table/table.fields';
import { TableColumn, TableDeleteEvent } from '../../../../../../shared/components/kit/table/table.types';
import { ActionButtonsComponent } from '../../../../../../../components/kit/action-buttons/action-buttons.component';
import { BaseDialogComponent } from '../../../../../../../components/kit/base-dialog/base-dialog.component';
import { BaseDialogService } from '../../../../../../../components/kit/base-dialog/base-dialog-service';
import { OrganizationPlatformBillingDiscountUpsertDialogComponent } from '../organization-platform-billing-discount-upsert-dialog/organization-platform-billing-discount-upsert-dialog.component';
import { PlatformBillingDiscountTableItem } from '../organization-platform-billing.types';
import { OrganizationPlatformBillingDiscountsDialogStore } from './organization-platform-billing-discounts-dialog.store';

@Component({
    selector: 'app-organization-platform-billing-discounts-dialog',
    templateUrl: './organization-platform-billing-discounts-dialog.component.html',
    providers: [OrganizationPlatformBillingDiscountsDialogStore],
    imports: [TableComponent, PrimeTemplate, ActionButtonsComponent],
})
export class OrganizationPlatformBillingDiscountsDialogComponent extends BaseDialogComponent {
    protected readonly store = inject(OrganizationPlatformBillingDiscountsDialogStore);
    private readonly dialogService = inject(BaseDialogService);
    private readonly confirm = inject(ConfirmBuilder);
    protected readonly organizationId = this.dialogConfig.data.organizationId as number;
    protected readonly contractId = this.dialogConfig.data.contractId as number;
    protected readonly canCreate = isAllowed['organizationBilling.create'];
    protected readonly canUpdate = isAllowed['organizationBilling.update'];
    protected readonly canDelete = isAllowed['organizationBilling.delete'];
    protected readonly prefix = 'organization.platformBilling.';
    private changed = false;

    protected readonly items = computed<PlatformBillingDiscountTableItem[]>(() =>
        (this.store.data.organizationOverview()?.discounts ?? [])
            .filter((discount) => discount.contractId === this.contractId)
            .map((discount) => ({
                id: discount.id,
                discount,
                discountPercentage: discount.percentage,
                validFrom: discount.validFrom,
                validTo: discount.validTo,
            })),
    );
    protected readonly columns: TableColumn[] = [
        numberField('discountPercentage'),
        validFromColumn,
        validToColumn,
    ];

    constructor() {
        super();
        void this.reload();
    }

    protected openUpsertDialog(item: PlatformBillingDiscountTableItem | null = null): void {
        this.dialogService.open(OrganizationPlatformBillingDiscountUpsertDialogComponent, {
            header: translate.instant(`${this.prefix}discountDialog.${item ? 'titleEdit' : 'titleCreate'}`),
            styleClass: 'p-dialog-width-60',
            data: {
                item: item?.discount ?? null,
                contractOptions: [],
                contractId: this.contractId,
            },
        }).onClose.subscribe((saved) => {
            if (!saved) return;
            this.changed = true;
            void this.reload();
        });
    }

    protected deleteDiscount(event: TableDeleteEvent<PlatformBillingDiscountTableItem>): void {
        this.confirm.confirmDelete(async () => {
            await this.store.api.deletePlatformBillingDiscount({ id: event.item.id });
            this.changed = true;
            await this.reload();
            successDeleted();
        }, event);
    }

    protected close(): void {
        this.dialogRef.close(this.changed);
    }

    private async reload(): Promise<void> {
        await this.store.api.organizationOverview(this.organizationId);
    }

}
